/**
 * Conformance fixtures: one turn as the runner streams it, and the failure
 * bodies it sends. An application's client tests fold these through its
 * own wrapper and assert what it renders, so every consumer proves the same
 * contract.
 */
import { sseFrame, SSE_KEEPALIVE } from './sse.js';
export const FIXTURE_SESSION_ID = 'chat-7-abc';
export const FIXTURE_SET_ID = '5555555555555555';
export const FIXTURE_REQUEST_ID = 'aaaaaaaaaaaaaaaa';
/** Send identity is metadata, separate from a form's request_id and event UUID. */
export const IDENTIFIED_SEND_EVENTS = [
    { type: 'user', uuid: 'original-user', request_id: 'send-one', message: { content: 'Review' } },
    { type: 'end', uuid: 'finished', status: 'success' },
];
/**
 * A turn with interim commentary, a tool call and its result, streamed
 * text, a form, and a published input set. Rendered, it is one user bubble
 * and one assistant bubble with one form.
 */
export const TURN_EVENTS = [
    { type: 'connect', session_id: FIXTURE_SESSION_ID, status: 'running', turn_mode: 'started' },
    { type: 'init', uuid: 'ev-init', session_id: FIXTURE_SESSION_ID, harness: 'codex', agent: 'personal', resumed: false },
    { type: 'user', uuid: 'ev-user-1', timestamp: 1_756_800_000_000, message: { content: 'Book a table' } },
    // Interim commentary: streamed, never completed, absent from the transcript.
    { type: 'assistant_delta', uuid: 'ev-delta-0', item_id: 'msg-0', text: '**Searching**' },
    {
        type: 'assistant',
        uuid: 'ev-assistant-tool',
        message: { content: [{ type: 'tool_use', id: 'call-1', name: 'search_places', input: { near: 'here' } }] },
    },
    { type: 'tool_started', uuid: 'ev-tool-1', id: 'call-1', name: 'search_places' },
    { type: 'progress', uuid: 'ev-progress-1', id: 'call-1', text: 'Looking up restaurants' },
    {
        type: 'user',
        uuid: 'ev-tool-result-1',
        message: { content: [{ type: 'tool_result', tool_use_id: 'call-1', content: '2 places' }] },
    },
    { type: 'assistant_delta', uuid: 'ev-delta-1', item_id: 'msg-1', text: 'Two ' },
    { type: 'assistant_delta', uuid: 'ev-delta-2', item_id: 'msg-1', text: 'options.' },
    {
        type: 'assistant',
        uuid: 'ev-assistant-1',
        timestamp: 1_756_800_004_000,
        message: {
            content: [
                { type: 'text', text: 'Two options.\n\ninput://aaaaaaaaaaaaaaaa\n' },
                {
                    type: 'form',
                    request_id: FIXTURE_REQUEST_ID,
                    set_id: FIXTURE_SET_ID,
                    kind: 'question',
                    title: 'Which one?',
                    prompt: 'Pick a restaurant',
                    schema: { fields: [{ name: 'choice', type: 'string' }] },
                },
            ],
        },
    },
    { type: 'end', uuid: 'ev-end-1', status: 'success', result: 'Two options.', input_set_id: FIXTURE_SET_ID },
];
/** The same turn as bytes on the wire, keepalive included, last frame unterminated. */
export function turnStream() {
    const frames = TURN_EVENTS.map((event) => sseFrame(event));
    frames.splice(3, 0, SSE_KEEPALIVE);
    const joined = frames.join('');
    return joined.slice(0, -1);
}
/** Replay of the same session after the client reconnected: no `connect`, same uuids. */
export const REPLAY_EVENTS = TURN_EVENTS.filter((event) => event.type !== 'connect');
/** A resume that finds the session bound to another harness. */
export const HARNESS_MISMATCH_EVENTS = [
    { type: 'init', uuid: 'ev-init-2', session_id: FIXTURE_SESSION_ID, harness: 'claude-code' },
    { type: 'error', uuid: 'ev-error-2', reason: 'harness_mismatch', message: 'Session is bound to codex' },
    { type: 'end', uuid: 'ev-end-2', status: 'failed' },
];
/** A turn the user cancelled after some text arrived. */
export const CANCELLED_EVENTS = [
    { type: 'init', uuid: 'ev-init-3', session_id: FIXTURE_SESSION_ID, harness: 'codex' },
    { type: 'user', uuid: 'ev-user-3', message: { content: 'Never mind' } },
    { type: 'assistant_delta', uuid: 'ev-delta-3', item_id: 'msg-3', text: 'Sure, ' },
    { type: 'end', uuid: 'ev-end-3', status: 'cancelled' },
];
/** A resume whose cursor the runner no longer holds: forget the cursor, reload history, never resend. */
export const CURSOR_UNKNOWN_EVENTS = [
    { type: 'connect', session_id: FIXTURE_SESSION_ID, status: 'completed' },
    { type: 'error', session_id: FIXTURE_SESSION_ID, reason: 'cursor_unknown', message: "Unknown replay cursor 'ev-gone'; reload the transcript" },
    { type: 'stream_end', session_id: FIXTURE_SESSION_ID, status: 'completed' },
];
/**
 * Refusal bodies exactly as the runner sends them, with the code each means.
 * The plain-string `busy` and `input_disabled` rows are what runners before
 * the coded bodies sent; both shapes must decode alike.
 */
export const REFUSALS = [
    { status: 409, body: { detail: { error: 'busy', message: 'Session is still running' } }, code: 'busy' },
    { status: 409, body: { detail: 'Session is still running' }, code: 'busy' },
    { status: 409, body: { detail: { error: 'request_already_admitted' } }, code: 'request_already_admitted' },
    { status: 409, body: { detail: { error: 'request_admission_unconfirmed' } }, code: 'request_admission_unconfirmed' },
    { status: 409, body: { detail: { error: 'request_payload_conflict' } }, code: 'request_payload_conflict' },
    { status: 503, body: { detail: { error: 'admission_unavailable' } }, code: 'admission_unavailable' },
    { status: 503, body: { detail: { error: 'runner_updating' } }, code: 'runner_updating' },
    { status: 409, body: { detail: { error: 'harness_mismatch', bound_harness: 'codex' } }, code: 'harness_mismatch' },
    { status: 409, body: { detail: { error: 'pointer_invalid' } }, code: 'pointer_invalid' },
    { status: 400, body: { detail: { error: 'model_harness_mismatch' } }, code: 'model_harness_mismatch' },
    { status: 400, body: { detail: { error: 'model_required' } }, code: 'model_required' },
    { status: 400, body: { detail: { error: 'input_disabled', message: 'Human input is not enabled on this runner' } }, code: 'input_disabled' },
    { status: 400, body: { detail: 'Human input is not enabled on this runner' }, code: 'input_disabled' },
    { status: 400, body: { detail: 'Invalid session id' }, code: 'invalid_request' },
    { status: 401, body: { detail: 'Invalid agent runner token' }, code: 'unauthorized' },
    { status: 404, body: { detail: 'Session not found' }, code: 'not_found' },
    { status: 422, body: { detail: [{ loc: ['body', 'prompt'], msg: 'field required' }] }, code: 'validation' },
    { status: 502, body: { detail: 'Could not steer session' }, code: 'worker_unavailable' },
    { status: 503, body: { detail: { error: 'harness_unavailable' } }, code: 'harness_unavailable' },
    { status: 503, body: '<html>bad gateway</html>', code: 'unavailable' },
    { status: 500, body: { detail: 'boom' }, code: 'runner_error' },
];
/**
 * The newest transcript page of the same session, as the transcript route
 * returns it: the durable events including the tool result, so the page
 * settles the same activity timeline the live stream did.
 */
export const TRANSCRIPT_PAGE = {
    events: REPLAY_EVENTS.filter((event) => ['user', 'assistant', 'progress', 'end', 'error'].includes(String(event.type))),
    page: { before_uuid: 'ev-user-1', has_more: true },
    last_uuid: 'ev-end-1',
    status: 'completed',
    active: false,
    recoverable: false,
};
/** The page before it: one finished turn without a form. */
export const OLDER_TRANSCRIPT_PAGE = {
    events: [
        { type: 'user', uuid: 'ev-user-0', message: { content: 'Hello' } },
        { type: 'assistant', uuid: 'ev-assistant-0', message: { content: [{ type: 'text', text: 'Hi there.' }] } },
        { type: 'end', uuid: 'ev-end-0', status: 'success', result: 'Hi there.' },
    ],
    page: { before_uuid: null, has_more: false },
    last_uuid: 'ev-end-1',
    status: 'completed',
    active: false,
    recoverable: false,
};
/** The status route's answers and the state each implies. */
export const STATUSES = [
    { payload: { status: 'running', active: true, recoverable: false }, state: 'active', plan: 'tail' },
    { payload: { status: 'pending', active: true, recoverable: false }, state: 'active', plan: 'tail' },
    { payload: { status: 'recoverable', active: false, recoverable: true }, state: 'recoverable', plan: 'replay' },
    { payload: { status: 'completed', active: false, recoverable: false }, state: 'terminal', plan: 'replay' },
    { payload: { status: 'failed', active: false, recoverable: false }, state: 'terminal', plan: 'replay' },
    { payload: { status: 'missing', active: false, recoverable: false }, state: 'missing', plan: 'idle' },
];
